import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, ButtonGroup, Input, Modal, Text, usePortalState, Tooltip } from '@hawkins/components';
import { IconCircleExclamationPoint } from '@hawkins/assets';
import { useTranslation } from 'react-i18next';
import NetflixGradientBackground from 'src/components/elements/NetflixGradientBackground';
import { AnnouncementBanner } from 'src/components/elements/AnnouncementBanner';
import { getErrorMessage } from 'src/utils/errorHandler';

function RequestResetPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const isStudioVersion = searchParams.get('version')?.trim().replace(/\/+$/, '') === 'studio';
  const [email, setEmail] = useState('');
  const [isOpen, { close, onClose, open }] = usePortalState();
  const [modalContent, setModalContent] = useState({
    title: '',
    message: '',
    type: 'info' as 'success' | 'error' | 'info'
  });


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Password reset requested for:', email);
    
    try {
      const response = await fetch('/api/request-password-reset-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userName: email
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        console.log('Password reset email sent successfully');
        setModalContent({
          title: t('common.status.success'),
          message: t('auth.requestReset.successMessage'),
          type: 'success'
        });
        open();
      } else {
        console.error('Failed to send password reset email:', data);
        setModalContent({
          title: t('common.status.error'),
          message: getErrorMessage(t, data),
          type: 'error'
        });
        open();
      }
    } catch (error) {
      console.error('Error sending password reset email:', error);
      setModalContent({
        title: t('common.status.error'),
        message: t('errors.network'),
        type: 'error'
      });
      open();
    }
  };

  return (
    <NetflixGradientBackground>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '20px',
        paddingTop: 'clamp(80px, 15vh, 120px)'
      }}>
      <div style={{
        position: 'absolute',
        top: 'clamp(100px, 12vh, 140px)',
        width: '100%',
        display: 'flex',
        justifyContent: 'center'
      }}>
        <img 
          src="/nflx-logo-classic@150w.png" 
          alt="Netflix" 
          style={{ height: '80px' }}
        />
      </div>

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        maxWidth: '450px'
      }}>
        <div style={{
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          padding: '48px 68px 36px 68px',
          borderRadius: '4px',
          width: '100%',
          minWidth: '350px'
        }}>
        <Text 
          style={{ 
            color: 'white', 
            fontSize: '32px', 
            fontWeight: '700',
            marginBottom: '24px',
            textAlign: 'center'
          }}
        >
          {t('auth.requestReset.title')}
        </Text>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
              <Text 
                style={{ 
                  color: 'white', 
                  fontSize: '16px',
                  marginRight: '8px'
                }}
              >
                {t('auth.requestReset.emailLabel')}
              </Text>
              <Tooltip label={t('auth.requestReset.usernameTooltip')}>
                <IconCircleExclamationPoint size="small" style={{ color: '#b3b3b3' }} />
              </Tooltip>
            </div>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={isStudioVersion ? t('auth.requestReset.studioEmailPlaceholder') : ""}
              required
              style={{
                width: '100%',
                backgroundColor: '#333',
                border: '1px solid #737373',
                borderRadius: '4px',
                color: 'white',
                fontSize: '16px',
                padding: '14px 20px',
                marginBottom: '24px',
                height: '50px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <Button
            type="submit"
            variant="primary"
            style={{
              width: '100%',
              backgroundColor: '#e50914',
              border: 'none',
              borderRadius: '4px',
              color: 'white',
              fontSize: '16px',
              fontWeight: '700',
              padding: '12px 16px',
              cursor: 'pointer',
              height: '48px',
              minHeight: '48px',
              boxSizing: 'border-box'
            }}
          >
            {t('auth.requestReset.submitButton')}
          </Button>
        </form>
        </div>

        {isStudioVersion && (
        <div style={{
          marginTop: '24px',
          textAlign: 'center'
        }}>
          <Text style={{ 
            color: 'white', 
            fontSize: '14px',
            opacity: 0.8
          }}>
            {t('auth.requestReset.supportText')}{' '}
            <a 
              href="mailto:support@netflixstudios.com"
              style={{ 
                color: '#0080ff', 
                textDecoration: 'none' 
              }}
            >
              support@netflixstudios.com
            </a>
          </Text>
        </div>
        )}
      </div>
      </div>

      <Modal
        open={isOpen}
        onClose={({ event }) => onClose({ event })}
      >
        <Modal.Header 
          title={modalContent.title}
          compact
        />
        <Modal.Body>
          <Text variant="body">
            {modalContent.message}
          </Text>
        </Modal.Body>
        <Modal.Footer>
          <ButtonGroup justifyContent="flex-end">
            <Button 
              onClick={close} 
              variant="primary"
            >
              {t('common.actions.ok')}
            </Button>
          </ButtonGroup>
        </Modal.Footer>
      </Modal>

      <AnnouncementBanner enabled={true} />
    </NetflixGradientBackground>
  );
}

export default RequestResetPage;